import React, { useMemo, useState, useContext, useRef, useCallback, useEffect } from "react";
import { ArrowBack, Replay, AdsClick } from "@mui/icons-material";
import { PAGE, usePageNavigation, NAVIGATION_DIRECTION } from "../contexts/PageNavigationContext";
import { usePageConfiguration } from "../contexts/PageConfigurationContext";
import CommonService from "@services/CommonService";
import DynamicHtml from "@common/DynamicHtml";
import { CONSTANT, HIERARCHY_FIELD_MAPPING } from "../constant";
import { useMediaQuery } from "@mui/material";
import { HtmlContentLoader, NavigationButton } from "@shared";
import Helper from "../Helper";
import { ConfigurationContext } from "@contexts/Configuration";
import { SearchContext } from "@contexts/Search";
import CoverageService from "@services/CoverageService";
import { NavigationSection, SearchSection, PlanSearchSection, PostSearchSection } from "./sections";
import LoaderWithIcon from "@common/LoaderWithIcon";
import { PendoService } from "@services";

// Custom hook for container dimensions
const useContainerDimensions = () => {
  const [width, setWidth] = useState(() => window.innerWidth);

  useEffect(() => {
    const getWidth = () => {
      try {
        const element = document.getElementById('coverageFinder');
        return element?.offsetWidth ?? window.innerWidth;
      } catch {
        return window.innerWidth;
      }
    };

    const updateWidth = () => {
      const newWidth = getWidth();
      setWidth(prev => prev !== newWidth ? newWidth : prev);
    };

    let timeoutId;
    const debouncedUpdate = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateWidth, 100);
    };

    updateWidth();

    if (window.ResizeObserver) {
      const observer = new ResizeObserver(debouncedUpdate);
      const element = document.getElementById('coverageFinder');
      element ? observer.observe(element) : window.addEventListener('resize', debouncedUpdate);

      return () => {
        clearTimeout(timeoutId);
        observer.disconnect();
        window.removeEventListener('resize', debouncedUpdate);
      };
    }

    window.addEventListener('resize', debouncedUpdate);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', debouncedUpdate);
    };
  }, []);

  return width;
};

export const SearchPageOrder = {
  PreSearch: 1,
  PostSearch: 2,
};

// Default page configuration
const DEFAULT_PAGE_CONFIG = {
  channel: { channelName: "" },
  safeChannelName: "",
  backButtonLabel: "",
  startOverButtonLabel: "",
  headerText: "[channel]",
  subHeaderText: "",
  footerText: "",
  sideHtmlDesktopUrl: "",
  sideHtmlMobileUrl: "",
  sideHtmlPercentWidth: `${CONSTANT.DEFAULT_DESKTOP_SIDE_HTML_WIDTH_PERCENT}%`,
  sideHtmlDesktopPosition: "",
  sideHtmlMobilePosition: "",
  resultHierarchyName: "Plan level",
  hierarchyShortName: "Plan",
  geographyType: "Zip code or State",
  noResultsMessage: "We could not find a result with that [hierarchy] name.",
  locationLabel: "Location",
  resultNameLabel: "Plan",
  isSearchRequired: true,
  isPlanSearchRequired: false,
  isNationalGeography: false,
  hideLocation: false,
  cardResultName: "",
  coverageHtmlDesktopUrl: "",
  coverageHtmlMobileUrl: "",
  noResultsType: "",
};

const useSearchState = () => {
  const [geoLocationValue, setGeoLocationValue] = useState({ geoInput: "", zipCode: "", state: "" });
  const [plans, setPlans] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [resultName, setResultName] = useState("");
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const [isPlanSearchEnabled, setIsPlanSearchEnabled] = useState(false);
  const [showPostSearch, setShowPostSearch] = useState(false);
  const [locationErrorMessage, setLocationErrorMessage] = useState("");
  const [highlightPlanSearch, setHighlightPlanSearch] = useState(false);
  const [highlightGeoInput, setHighlightGeoInput] = useState(true);
  const [showNoResults, setShowNoResults] = useState(false);
  return {
    geoLocationValue, setGeoLocationValue,
    plans, setPlans,
    selectedResult, setSelectedResult,
    resultName, setResultName,
    isSearchEnabled, setIsSearchEnabled,
    isPlanSearchEnabled, setIsPlanSearchEnabled,
    showPostSearch, setShowPostSearch,
    locationErrorMessage, setLocationErrorMessage,
    highlightPlanSearch, setHighlightPlanSearch,
    highlightGeoInput, setHighlightGeoInput,
    showNoResults, setShowNoResults
  };
};

const usePageConfigurationHook = (pageConfig, showPostSearch) => {
  return useMemo(() => {
    if (!pageConfig?.generalSetting) {
      return {
        ...DEFAULT_PAGE_CONFIG,
        backButtonIcon: <ArrowBack />,
        startOverButtonIcon: <Replay />,
        changeSelectedResultButtonIcon: <AdsClick />,
      };
    }

    const preSearchConfig = pageConfig?.searchPageConfigs.find((p) => p.pageOrder == SearchPageOrder.PreSearch);
    const postSearchConfig = pageConfig?.searchPageConfigs.find((p) => p.pageOrder == SearchPageOrder.PostSearch);
    const hierarchyShortName = HIERARCHY_FIELD_MAPPING[pageConfig?.searchDetails?.resultHierarchyName?.toLowerCase()]?.ShortName || "";

    return {
      channel: pageConfig?.channel,
      safeChannelName: CommonService.getSafeAlphaNumericText(pageConfig?.channel?.channelName),
      backButtonLabel: preSearchConfig?.backButtonName,
      startOverButtonLabel: preSearchConfig?.startOverButtonName,
      backButtonIcon: CommonService.getIcon(pageConfig?.generalSetting?.backButtonIcon, pageConfig.clientId, <ArrowBack />),
      startOverButtonIcon: CommonService.getIcon(pageConfig?.generalSetting?.startOverButtonIcon, pageConfig.clientId, <Replay />),
      changeSelectedResultButtonIcon: showPostSearch ? CommonService.getIcon(pageConfig?.generalSetting?.changeSelectedResultButtonIcon, pageConfig.clientId, <AdsClick />) : null,
      changeSelectedResultButtonLabel: showPostSearch ? Helper.replacePlaceholderWithValues(postSearchConfig?.selectedResultLabel, { hierarchy: hierarchyShortName }) : "",
      headerText: showPostSearch ? postSearchConfig?.header : preSearchConfig?.header,
      subHeaderText: showPostSearch ? postSearchConfig?.subheader : preSearchConfig?.subheader,
      footerText: showPostSearch ? postSearchConfig?.footer : preSearchConfig?.footer,
      sideHtmlDesktopUrl: CommonService.getHtmlFileUrl(showPostSearch ? postSearchConfig?.sideHtmlFile : preSearchConfig?.sideHtmlFile, pageConfig.clientId),
      sideHtmlMobileUrl: CommonService.getHtmlFileUrl(showPostSearch ? postSearchConfig?.mobileSideHtmlFile : preSearchConfig?.mobileSideHtmlFile, pageConfig.clientId),
      sideHtmlPercentWidth: showPostSearch ? postSearchConfig?.sideHtmlScreenSizePercent : preSearchConfig?.sideHtmlScreenSizePercent,
      sideHtmlDesktopPosition: showPostSearch ? postSearchConfig?.sideHtmlPosition : preSearchConfig?.sideHtmlPosition,
      sideHtmlMobilePosition: showPostSearch ? postSearchConfig?.mobileSideHtmlPosition : preSearchConfig?.mobileSideHtmlPosition,
      // Search Details
      resultHierarchyName: pageConfig?.searchDetails?.resultHierarchyName,
      hierarchyShortName: hierarchyShortName,
      geographyType: pageConfig?.searchDetails?.geographyType,
      noResultsMessage: Helper.replacePlaceholderWithValues(pageConfig?.searchDetails?.noResultsMessage, { hierarchy: hierarchyShortName }),
      noResultsType: pageConfig?.searchDetails?.noResultsType?.toLowerCase() || "",
      locationLabel: pageConfig?.searchDetails?.locationLabel,
      resultNameLabel: pageConfig?.searchDetails?.resultNameLabel,
      isSearchRequired: Helper.IsSearchRequired(pageConfig?.searchDetails?.geographyType),
      isPlanSearchRequired: Helper.IsPlanSearchRequired(pageConfig?.searchDetails?.geographyType),
      isNationalGeography: pageConfig?.searchDetails?.geographyType?.toLowerCase().includes('national'),
      //Coverage DataField
      hideLocation: showPostSearch ? pageConfig?.coverageCardFields?.hideButtonResult : false,
      cardResultName: showPostSearch ? pageConfig?.coverageCardFields?.cardResultName : "",
      //Coverage Card Html
      coverageHtmlDesktopUrl: showPostSearch ? CommonService.getHtmlFileUrl(postSearchConfig?.htmlFile2, pageConfig.clientId) : "",
      coverageHtmlMobileUrl: showPostSearch ? CommonService.getHtmlFileUrl(postSearchConfig?.mobileHtmlFile2, pageConfig.clientId) : "",
      searchPageState: showPostSearch ? "post" : "pre",
    };
  }, [pageConfig, showPostSearch]);
};

const useCoverageSearch = (pageConfig, channel, geoLocationValue, resultHierarchyName, states, isNationalGeography, setPlans, setIsPlanSearchEnabled, setHighlightPlanSearch, setShowPostSearch, setShowNoResults) => {
  const [isLoading, setIsLoading] = useState(false);

  const getHierarchyOptions = useCallback(async () => {
    try {
      setIsLoading(true);
      setShowPostSearch(false);
      const hierarchyRequest = {
        ProjectId: pageConfig?.projectId,
        LivesTypeId: pageConfig?.benefitTypeId === 1 ? 1 : 4,
        ChannelName: channel?.channelName,
        HierarchyName: resultHierarchyName,
        CoverageSortId: pageConfig?.searchDetails?.coverageSortId || 0 
      };
      const hierarchyResponse = await CoverageService.getInstance().getHierarchyOptions(hierarchyRequest);
      if (hierarchyResponse != null && hierarchyResponse.hierarchyOptions != null && Array.isArray(hierarchyResponse.hierarchyOptions) && hierarchyResponse.hierarchyOptions.length > 0) {
        setPlans(hierarchyResponse.hierarchyOptions);
        setIsPlanSearchEnabled(true);
        setHighlightPlanSearch(true);
        setShowNoResults(false);
      } else {
        setPlans([]);
        setIsPlanSearchEnabled(false);
        setHighlightPlanSearch(false);
        setShowNoResults(true);
      }
    } catch (error) {
      console.error("Error in getHierarchyOptions:", error);
      setPlans([]);
      setIsPlanSearchEnabled(false);
      setHighlightPlanSearch(false);
      setShowNoResults(true);
    } finally {
      setIsLoading(false);
    }
  }, [pageConfig, channel, resultHierarchyName, setPlans, setIsPlanSearchEnabled, setHighlightPlanSearch, setShowPostSearch, setShowNoResults]);

  const searchCoverage = useCallback(async () => {
    try {
      setIsLoading(true);
      setShowPostSearch(false);
      const coverageRequest = {
        ProjectId: pageConfig?.projectId,
        ProductId: pageConfig?.productId,
        IndicationId: pageConfig?.indicationId,
        LiveTypeId: pageConfig?.benefitTypeId === 1 ? 1 : 4,
        ChannelName: channel?.channelName,
        ZipCode: geoLocationValue?.zipCode || null,
        StateId: states?.find((state) => state.name.toLowerCase() === geoLocationValue?.state.toLowerCase())?.stateId || null,
        ResultName: resultHierarchyName,
        IsNationalGeography: isNationalGeography
      };
      const coverageResponse = await CoverageService.getInstance().getPatientFinderCoverage(coverageRequest);
      if (coverageResponse != null && coverageResponse.coverages != null && Array.isArray(coverageResponse.coverages) && coverageResponse.coverages.length > 0) {
        setPlans(coverageResponse.coverages);
        setIsPlanSearchEnabled(true);
        setHighlightPlanSearch(true);
        setShowNoResults(false);
      } else {
        setPlans([]);
        setIsPlanSearchEnabled(false);
        setHighlightPlanSearch(false);
        setShowNoResults(true);
      }
    } catch (error) {
      console.error("Error in searchCoverage:", error);
      setPlans([]);
      setIsPlanSearchEnabled(false);
      setHighlightPlanSearch(false);
      setShowNoResults(true);
    } finally {
      setIsLoading(false);
    }
  }, [pageConfig, channel, geoLocationValue, resultHierarchyName, states, isNationalGeography, setPlans, setIsPlanSearchEnabled, setHighlightPlanSearch, setShowPostSearch, setShowNoResults]);

  const searchCoverageWithHierarchyOption = useCallback(async (hierarchyOptionId) => {
    try {
      setIsLoading(true);
      const coverageRequest = {
        ProjectId: pageConfig?.projectId,
        ProductId: pageConfig?.productId,
        IndicationId: pageConfig?.indicationId,
        LiveTypeId: pageConfig?.benefitTypeId === 1 ? 1 : 4,
        ChannelName: channel?.channelName,
        ZipCode: null,
        StateId: null,
        ResultName: resultHierarchyName,
        HierarchyOptionId: hierarchyOptionId
      };
      const coverageResponse = await CoverageService.getInstance().getPatientFinderCoverage(coverageRequest);
      if (coverageResponse != null && coverageResponse.coverages != null && Array.isArray(coverageResponse.coverages) && coverageResponse.coverages.length > 0) {
        return coverageResponse.coverages[0];
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error in searchCoverageWithHierarchyOption:", error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [pageConfig, channel, resultHierarchyName]);

  return { searchCoverage, getHierarchyOptions, searchCoverageWithHierarchyOption, isLoading };
};

const ChannelSearchPage = () => {
  const { navigateBackward, startOver, navigationDirection } = usePageNavigation();
  const { getPageConfig, getMobileBreakpoint } = usePageConfiguration();
  const { appliedConfiguration } = useContext(ConfigurationContext);
  const pageConfig = getPageConfig(PAGE.CHANNEL_SEARCH);
  const matchesMobileWidth = useMediaQuery(getMobileBreakpoint());
  const searchButtonRef = useRef();
  const containerWidth = useContainerDimensions();
  const { productId, productName, indicationName } = useContext(SearchContext);

  const {
    geoLocationValue, setGeoLocationValue,
    plans, setPlans,
    selectedResult, setSelectedResult,
    resultName, setResultName,
    isSearchEnabled, setIsSearchEnabled,
    isPlanSearchEnabled, setIsPlanSearchEnabled,
    showPostSearch, setShowPostSearch,
    locationErrorMessage, setLocationErrorMessage,
    highlightPlanSearch, setHighlightPlanSearch,
    highlightGeoInput, setHighlightGeoInput,
    showNoResults, setShowNoResults
  } = useSearchState();

  const states = useMemo(() => {
    if (!appliedConfiguration?.states) return [];
    return [...appliedConfiguration.states].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: 'base' })
    );
  }, [appliedConfiguration?.states]);

  const pageConfiguration = usePageConfigurationHook(pageConfig, showPostSearch);

  const {
    channel,
    safeChannelName,
    backButtonLabel,
    startOverButtonLabel,
    backButtonIcon,
    startOverButtonIcon,
    changeSelectedResultButtonIcon,
    changeSelectedResultButtonLabel,
    headerText,
    subHeaderText,
    footerText,
    sideHtmlDesktopUrl,
    sideHtmlMobileUrl,
    sideHtmlPercentWidth,
    sideHtmlDesktopPosition,
    sideHtmlMobilePosition,
    resultHierarchyName,
    hierarchyShortName,
    geographyType,
    noResultsMessage,
    noResultsType,
    locationLabel,
    resultNameLabel,
    isSearchRequired,
    isPlanSearchRequired,
    isNationalGeography,
    hideLocation,
    cardResultName,
    coverageHtmlDesktopUrl,
    coverageHtmlMobileUrl,
    searchPageState,
  } = pageConfiguration;

  const { searchCoverage, getHierarchyOptions, searchCoverageWithHierarchyOption, isLoading } = useCoverageSearch(pageConfig, channel, geoLocationValue, resultHierarchyName, states, isNationalGeography, setPlans, setIsPlanSearchEnabled, setHighlightPlanSearch, setShowPostSearch, setShowNoResults);

  const [performNationalSearch, setPerformNationalSearch] = useState(isNationalGeography);
  useEffect(() => {
    if (performNationalSearch && navigationDirection === NAVIGATION_DIRECTION.NONE) {
      (async () => await getHierarchyOptions())();
      setPerformNationalSearch(false);
    }
  }, [navigationDirection, performNationalSearch, getHierarchyOptions]);

  // Separate effect to handle hierarchy option coverage call
  const [hierarchyCoverageResult, setHierarchyCoverageResult] = useState(null);
  
  useEffect(() => {
    const handleHierarchyOptionCoverage = async () => {
      // Check if this is a HierarchyOption response (from national search)
      const isHierarchyOption = selectedResult?.hierarchyOptionId;
      if (isHierarchyOption && showPostSearch) {
        const hierarchyOptionId = selectedResult.hierarchyOptionId;
        const coverageResult = await searchCoverageWithHierarchyOption(hierarchyOptionId);
        setHierarchyCoverageResult(coverageResult);
      } else {
        setHierarchyCoverageResult(null);
      }
    };

    if (selectedResult) {
      handleHierarchyOptionCoverage();
    }
  }, [selectedResult, showPostSearch, searchCoverageWithHierarchyOption]);

  const generateCoverageCardHtml = useCallback((coverageData, pageConfig) => {
    if (!coverageData || !pageConfig?.coverageCardFields) return "";

    const { paDataField, stDataField, coverageStatusDataField } = pageConfig.coverageCardFields;
    if (!paDataField || !stDataField || !coverageStatusDataField) return "";

    const paDataFieldValue = coverageData[paDataField]?.toLowerCase() || "no";
    const stDataFieldValue = coverageData[stDataField]?.toLowerCase() || "no";

    const html = pageConfig.coverageCardValues?.find((c) =>
      CommonService.convertSafeJoinWithCommasToArray(c.coverageStatus.toLowerCase()).includes(coverageData[coverageStatusDataField]?.toLowerCase()) &&
      (!paDataField || paDataField.toLowerCase() === "none" || CommonService.convertSafeJoinWithCommasToArray(c.paValue?.toLowerCase()).includes(paDataFieldValue)) &&
      (!stDataField || stDataField.toLowerCase() === "none" || CommonService.convertSafeJoinWithCommasToArray(c.stValue?.toLowerCase()).includes(stDataFieldValue))
    )?.coverageCardHtml || "";
    
    return CommonService.replacePlaceholdersWithResults(html, coverageData);
  }, []);

  // Generate coverage card HTML based on the coverage result
  const coverageCardHtml = useMemo(() => {
    if (!selectedResult || !pageConfig?.coverageCardFields) return "";

    // Handle "no results found" case
    if (selectedResult == "noResultsFound") {
      return pageConfig?.coverageCardValues?.find((c) =>
        CommonService.convertSafeJoinWithCommasToArray(c.coverageStatus.toLowerCase()).includes("no results found")
      )?.coverageCardHtml || "";
    }

    // Check if this is a HierarchyOption response (from national search)
    const isHierarchyOption = selectedResult.hierarchyOptionId || selectedResult.HierarchyOptionId;
    if (isHierarchyOption) {
      // Use the coverage result from the hierarchy option call
      const coverageData = hierarchyCoverageResult;
      if (!coverageData) return "";
      return generateCoverageCardHtml(coverageData, pageConfig);
    }

    // Regular coverage logic for non-hierarchy options
    return generateCoverageCardHtml(selectedResult, pageConfig);
  }, [pageConfig, selectedResult, hierarchyCoverageResult, generateCoverageCardHtml]);

  const footerHtml = useMemo(() => {
    if (!showPostSearch) return footerText;
    const {coverageStatusDataField } = pageConfig?.coverageCardFields || {};
    
    // Use hierarchy coverage result if available, otherwise use selected result
    const isHierarchyOption = selectedResult?.hierarchyOptionId || selectedResult?.HierarchyOptionId;
    const dataSource = isHierarchyOption ? hierarchyCoverageResult : selectedResult;

    const coverageStatus = (selectedResult == "noResultsFound") 
                          ? CONSTANT.NO_RESULTS_FOUND 
                          : (coverageStatusDataField && dataSource ? dataSource[coverageStatusDataField] : "");
    
    const html = CommonService.getDynamicHtmlForCoverageStatus(footerText, coverageStatus);
    return (selectedResult == "noResultsFound") ? html : (dataSource ? CommonService.replacePlaceholdersWithResults(html, dataSource) : html);
  }, [footerText, selectedResult, hierarchyCoverageResult, showPostSearch, pageConfig]);

  const subHeaderHtml = useMemo(() => {
    if (!showPostSearch) return subHeaderText;
    const {coverageStatusDataField } = pageConfig?.coverageCardFields || {};
    
    // Use hierarchy coverage result if available, otherwise use selected result
    const isHierarchyOption = selectedResult?.hierarchyOptionId || selectedResult?.HierarchyOptionId;
    const dataSource = isHierarchyOption ? hierarchyCoverageResult : selectedResult;    
    
    const coverageStatus = (selectedResult == "noResultsFound") 
                          ? CONSTANT.NO_RESULTS_FOUND 
                          : (coverageStatusDataField && dataSource ? dataSource[coverageStatusDataField] : "");
    
    const html = CommonService.getDynamicHtmlForCoverageStatus(subHeaderText, coverageStatus);
    return (selectedResult == "noResultsFound") ? html : (dataSource ? CommonService.replacePlaceholdersWithResults(html, dataSource) : html);
  }, [subHeaderText, selectedResult, hierarchyCoverageResult, showPostSearch, pageConfig]);


  const handleLocationChange = useCallback((value) => {
    setGeoLocationValue(Helper.resolveGeoLocationValue(value, states));
    setHighlightGeoInput(false);
    setSelectedResult(null);
    setResultName("");
    setLocationErrorMessage("");
    setIsPlanSearchEnabled(false);
    setHighlightPlanSearch(false);
    setIsSearchEnabled(true);
    setShowPostSearch(false);
    setShowNoResults(false);
  }, [states]);


  const initiateCoverageSearch = useCallback(async () => {
    setSelectedResult(null);
    setResultName("");
    Helper.resetFocusToElement(searchButtonRef);
    const isValid = Helper.isGeoInputValid(geoLocationValue, geographyType, states);
    if (isValid) {
      setHighlightGeoInput(false);
      setLocationErrorMessage("");
      PendoService.getInstance().trackEvent("PF2 Location Search Click", "ChannelSearchPage", geoLocationValue.geoInput, productId, productName, indicationName);
      await searchCoverage();
    } else {
      setHighlightGeoInput(true);
      setLocationErrorMessage(Helper.getGeoErrorMessage(geographyType, geoLocationValue.geoInput));
    }
  }, [geoLocationValue, geographyType, states, searchCoverage]);

  const handleResultSearch = useCallback((option) => {
    const selectedResultName = Helper.retrieveResultName(option, resultHierarchyName);
    PendoService.getInstance().trackEvent("PF2 Plan Search", "ChannelSearchPage", selectedResultName, productId, productName, indicationName);
    setResultName(selectedResultName);
    setSelectedResult(option);
    setHighlightPlanSearch(false);
    setShowPostSearch(true);
  }, [resultHierarchyName]);

  const onAnchorClick = (url) => {
    const clickInfo = [geoLocationValue.geoInput, resultName].filter(Boolean).join(": ");
    PendoService.getInstance().trackEvent("PF2 Custom Link Click", "ChannelSearchPage > " + clickInfo, url, productId, productName, indicationName);
  };

  const resetSearchState = useCallback(() => {
    setSelectedResult(null);
    setResultName("");
    setIsSearchEnabled(false);
    setIsPlanSearchEnabled(false);
    setShowPostSearch(false);
    setHighlightPlanSearch(false);
    setHighlightGeoInput(true);
    setLocationErrorMessage("");
    setShowNoResults(false);
  }, []);

  const handleLocationClear = useCallback(() => {
    setGeoLocationValue({ geoInput: "", zipCode: "", state: "" });
    resetSearchState();
  }, [resetSearchState]);

  const handlePlanClear = useCallback(() => {
    setSelectedResult(null);
    setResultName("");
    setShowPostSearch(false);
    setHighlightPlanSearch(true);
  }, []);

  const handleChangeSelectedResult = useCallback(() => {
    setSelectedResult(null);
    setResultName("");
    setShowPostSearch(false);
    setHighlightPlanSearch(true);
  }, []);

  const sideHtml = useMemo(() => {
    const config = {
      sideHtmlDesktopUrl,
      sideHtmlMobileUrl,
      sideHtmlDesktopPosition,
      sideHtmlMobilePosition,
      sideHtmlPercentWidth
    };
    return Helper.getSideHtmlConfig(config, matchesMobileWidth);
  }, [sideHtmlDesktopUrl, sideHtmlMobileUrl, sideHtmlDesktopPosition, sideHtmlMobilePosition, sideHtmlPercentWidth, matchesMobileWidth]);

  const containerClassName = `norstella-pf-channelSearchPage-container ${sideHtml.containerClass} norstella-pf-${safeChannelName}-searchPage-container norstella-pf-${searchPageState}-searchPage-container`;

  return (
    <>
      {isLoading && (
        <div
          className="norstella-cf-overlay"
          style={matchesMobileWidth ? { width: '100vw' } : { width: `${containerWidth}px` }}>
          <LoaderWithIcon />
        </div>
      )}
      <div className={`norstella-pf-channelSearchPage-main-container norstella-pf-${safeChannelName}-main-container norstella-pf-${searchPageState}-main-container`}>
        {/* Side HTML Panel */}
        {sideHtml.showHtml && (
          <div className={`norstella-pf-SideHtmlLeftTop norstella-pf-${safeChannelName}-SideHtmlLeftTop norstella-pf-${searchPageState}-SideHtmlLeftTop`} style={{ width: sideHtml.width, order: sideHtml.htmlOrder }}>
            <HtmlContentLoader url={sideHtml.htmlUrl} onAnchorClick={onAnchorClick} />
          </div>
        )}
        {/* Main Content Container */}
        <div className={containerClassName} style={{ width: sideHtml.contentWidth, order: sideHtml.contentOrder }}>
          {/* Navigation Section */}
          <div className={`norstella-pf-row norstella-pf-row-nav norstella-pf-${safeChannelName}-row-nav norstella-pf-${searchPageState}-row-nav`}>
            <NavigationSection
              backButtonLabel={backButtonLabel}
              backButtonIcon={backButtonIcon}
              navigateBackward={navigateBackward}
              forwardButtonLabel={startOverButtonLabel}
              forwardButtonIcon={startOverButtonIcon}
              navigateForward={startOver}
              headerText={headerText}
              safeChannelName={safeChannelName}
              channel={channel}
              resultName={resultName}
              hierarchyShortName={hierarchyShortName}
              stateName={geoLocationValue.state}
              zipCode={geoLocationValue.zipCode}
              pageName={"channelSearch"}
              pageState={searchPageState}
            />
          </div>
          {showPostSearch && hideLocation && !matchesMobileWidth && (
            <div className={`norstella-pf-row norstella-pf-nav-changeSelectedResult-top norstella-pf-${safeChannelName}-nav-changeSelectedResult-top`}>
              <NavigationButton
                label={changeSelectedResultButtonLabel}
                icon={changeSelectedResultButtonIcon}
                className={"norstella-pf-changeSelectedResult-btn"}
                onClick={handleChangeSelectedResult}
                identifier={safeChannelName}
              />
            </div>
          )}
          {/* Search Section */}
          <SearchSection
            subHeaderText={subHeaderHtml}
            safeChannelName={safeChannelName}
            channel={channel}
            isSearchRequired={isSearchRequired}
            handleLocationChange={handleLocationChange}
            initiateCoverageSearch={initiateCoverageSearch}
            handleLocationClear={handleLocationClear}
            locationLabel={locationLabel}
            geographyType={geographyType}
            locationErrorMessage={locationErrorMessage}
            states={states}
            highlightGeoInput={highlightGeoInput}
            showPostSearch={showPostSearch}
            changeSelectedResultButtonLabel={changeSelectedResultButtonLabel}
            changeSelectedResultButtonIcon={changeSelectedResultButtonIcon}
            handleChangeSelectedResult={handleChangeSelectedResult}
            isSearchEnabled={isSearchEnabled}
            searchButtonRef={searchButtonRef}
            hideLocation={hideLocation}
            matchesMobileWidth={matchesMobileWidth}
            resultName={resultName}
            hierarchyShortName={hierarchyShortName}
            stateName={geoLocationValue.state}
            zipCode={geoLocationValue.zipCode}
            searchPageState={searchPageState}
          />
          {/* Plan Search Section */}
          <PlanSearchSection
            isPlanSearchRequired={isPlanSearchRequired}
            showPostSearch={showPostSearch}
            safeChannelName={safeChannelName}
            resultNameLabel={resultNameLabel}
            plans={plans}
            resultHierarchyName={resultHierarchyName}
            selectedResult={selectedResult}
            handleResultSearch={handleResultSearch}
            highlightPlanSearch={highlightPlanSearch}
            setHighlightPlanSearch={setHighlightPlanSearch}
            isPlanSearchEnabled={isPlanSearchEnabled}
            noResultsMessage={noResultsMessage}
            handlePlanClear={handlePlanClear}
            noResultsType={noResultsType}
            searchPageState={searchPageState}
          />
          {/* Post Search Section */}
          {!isLoading && (<PostSearchSection
            showPostSearch={showPostSearch}
            showNoResults={showNoResults}
            safeChannelName={safeChannelName}
            cardResultName={cardResultName}
            resultName={resultName}
            hierarchyShortName={hierarchyShortName}
            geoLocationValue={geoLocationValue}
            coverageCardHtml={coverageCardHtml}
            channel={channel}
            matchesMobileWidth={matchesMobileWidth}
            coverageHtmlMobileUrl={coverageHtmlMobileUrl}
            coverageHtmlDesktopUrl={coverageHtmlDesktopUrl}
            onAnchorClick={onAnchorClick}
          />)}
          {/* Footer Section */}
          {footerText && footerText.trim() !== "" && (
            <div className={`norstella-pf-row norstella-pf-row-footer norstella-pf-${safeChannelName}-row-footer norstella-pf-${searchPageState}-row-footer`}>
              <div className={`norstella-pf-footer-section norstella-pf-${safeChannelName}-footer-section norstella-pf-${searchPageState}-footer-section`}>
                <DynamicHtml input={footerHtml} className={"norstella-pf-footer"} part="pfDivFooterText"
                  channelName={channel.channelName}
                  resultname={resultName}
                  hierarchyShortName={hierarchyShortName}
                  stateName={geoLocationValue.state}
                  zipCode={geoLocationValue.zipCode} />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ChannelSearchPage;
